import React, { useEffect, useState } from "react"

import { QRCodeSVG } from "qrcode.react"
import { useNavigate } from "react-router-dom"
import { useClipboard } from "use-clipboard-copy"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { MFAType } from "../../../../config/auth.config"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { authSelector } from "../../../../store/selectors/auth.selector"
import {
    authProcessMfaCode,
    authSetUpTOTP,
} from "../../../../store/thunks/auth.thunks"
import { CodeForm } from "../../mfa/codeForm"

import checkIcon from "@front-shared/assets/images/icons/check-dark3.svg"
import copyIcon from "@front-shared/assets/images/icons/copy-dark.svg"

export const TOTPSetupStep: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const authState = useAppSelector(authSelector)
    const navigate = useNavigate()

    const clipboard = useClipboard()
    const [isCopied, setIsCopied] = useState<boolean>(false)

    useEffect(() => {
        dispatch(
            authSetUpTOTP({
                payload: null,
            }),
        )
    }, [])

    useEffect(() => {
        setIsCopied(clipboard.copied)
    }, [clipboard.copied])

    const onSubmit = (code: string) => {
        dispatch(
            authProcessMfaCode({
                payload: {
                    code,
                    withLoggingIn: true,
                    mfaType: MFAType.TOTP,
                },
                navigate,
            }),
        )
    }

    // Null check included for type safety; totpSetupDetails should be present on this step
    if (!authState.totpSetupDetails) {
        return null
    }

    return (
        <div className="relative">
            <PrimaryText
                className="whitespace-pre-wrap mb-[24px]"
                typography="h3"
            >
                Set up your Authenticator App
            </PrimaryText>

            <PrimaryText
                className="whitespace-pre-wrap mb-[20px]"
                typography="text"
                size={20}
            >
                To set up your authenticator app, follow the steps below.
            </PrimaryText>

            <PrimaryText
                className="whitespace-pre-wrap mb-[20px] ml-[10px]"
                typography="text"
            >
                1. Download an authenticator app like Authy or Google
                Authenticator.
            </PrimaryText>

            <PrimaryText
                className="whitespace-pre-wrap mb-[20px] ml-[10px]"
                typography="text"
            >
                2. Open the app and scan the QR code below.
            </PrimaryText>

            <div className="flex items-center gap-[16px] mb-[20px]">
                <QRCodeSVG
                    width={100}
                    height={100}
                    value={authState.totpSetupDetails.setupUri}
                    bgColor={NIRVANA_COLORS.brand.offWhite}
                />

                <div className="flex flex-col gap-[12px]">
                    <PrimaryText size={12} lineHeight={18}>
                        Or Manually enter code:
                    </PrimaryText>

                    <div className="max-w-[300px]">
                        <PrimaryText
                            size={14}
                            lineHeight={21}
                            weight="semibold"
                            className="break-all inline"
                        >
                            {authState.totpSetupDetails.sharedSecret}
                        </PrimaryText>

                        <img
                            className="inline cursor-pointer w-[16px] h-[16px] ml-[8px] align-baseline"
                            src={isCopied ? checkIcon : copyIcon}
                            alt="Copy"
                            onClick={() => {
                                clipboard.copy(
                                    authState.totpSetupDetails?.sharedSecret ||
                                        "",
                                )
                            }}
                        />
                    </div>
                </div>
            </div>

            <PrimaryText
                className="whitespace-pre-wrap mb-[20px] ml-[10px]"
                typography="text"
            >
                3. Enter the 6-digit code that is appearing on the app
            </PrimaryText>

            <CodeForm
                isCtaLoading={authState.isLoading}
                onSubmit={onSubmit}
                mfaType={MFAType.TOTP}
                submitText="Verify Code"
            />
        </div>
    )
}
